import React, {useEffect, useState} from "react";
import SvgLoader from "../../../../svg/SvgLoader";
import {useSocket} from "../../SocketContext";
import {rpc_getPlayerInventory} from "../../rpc";
import {PlayerInventory} from "./interfaces";
import InventoryGrid from "./InventoryGrid";
import {useOverlay} from "../../overlay/OverlayContext";
import {LimitErrorResponse} from "../../../../util/fetch";
import {
    LucideCircleSlash, LucideCircleX,
    LucidePackagePlus,
    LucideRefreshCcw
} from "lucide-react";

interface InventoryTabProps {
    steamId: string
    displayName: string
}

// Turn the backend/plugin error code into a readable message, falling back to the raw code.
function describeInventoryError(code?: string): string {
    switch (code) {
        case undefined:
            return "No inventory data available"
        case "not_connected":
            return "The game server is not connected to RustServerTool."
        case "timeout":
            return "The game server did not respond in time."
        case "player_not_found":
            return "Player not found — they may be offline."
        case "no_inventory":
            return "This player has no inventory."
        case "invalid_steam_id":
            return "Invalid Steam ID."
        case "no_permission":
            return "You do not have permission to view this inventory."
        default:
            return code
    }
}

export default function InventoryTab({steamId, displayName}: InventoryTabProps) {
    const socket = useSocket()
    const overlay = useOverlay()
    const [inventory, setInventory] = useState<PlayerInventory>()
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string>()
    // Only set when the game server never replied — i.e. the plugin is probably not installed.
    const [pluginUnresponsive, setPluginUnresponsive] = useState(false)
    // Set when the user's subscription tier doesn't permit the inventory viewer.
    const [limitError, setLimitError] = useState<LimitErrorResponse>()
    const [refreshTick, setRefreshTick] = useState(0)

    useEffect(() => {
        let cancelled = false
        setIsLoading(true)
        setError(undefined)
        setPluginUnresponsive(false)
        setLimitError(undefined)
        rpc_getPlayerInventory(socket, steamId)
            .then(result => {
                if (cancelled) return
                if (result?.limitError) {
                    setInventory(undefined)
                    setLimitError(result.limitError)
                    overlay.openErrorOverlay(result.limitError)
                } else if (result?.inventory) {
                    setInventory(result.inventory)
                } else {
                    setInventory(undefined)
                    setError(describeInventoryError(result?.error))
                    setPluginUnresponsive(result?.pluginUnresponsive ?? false)
                }
                setIsLoading(false)
            })
            .catch(err => {
                if (cancelled) return
                setInventory(undefined)
                setError(err?.toString() ?? "Failed to load inventory")
                setIsLoading(false)
            })
        return () => { cancelled = true }
        // the overlay object is a fresh reference each render, so adding it would cause a re-fetch loop
        // eslint-disable-next-line
    }, [socket, steamId, refreshTick])

    const refresh = () => setRefreshTick(t => t + 1)

    return (
        <div className="space-y-4">
            {/* ACTIONS */}
            <div className="flex flex-row items-center justify-end gap-2">
                <button
                    className="px-3 py-1 rounded-md bg-primary text-white hover:bg-primary-hover flex items-center"
                    onClick={() => overlay.openGiveItemOverlay({steamId, displayName})}>
                    <LucidePackagePlus size={16} className="me-2"/>
                    Give Item
                </button>
                <button
                    className="px-3 py-1 rounded-md bg-primary text-white hover:bg-primary-hover flex items-center disabled:opacity-50"
                    onClick={refresh}
                    disabled={isLoading}>
                    <LucideRefreshCcw size={16} className={`me-2 ${isLoading ? "animate-spin" : ""}`}/>
                    Refresh
                </button>
            </div>

            {isLoading && (
                <div className="flex flex-col items-center py-8">
                    <SvgLoader/>
                    <p className="mt-4 text-white text-lg font-semibold tracking-wide">Loading...</p>
                </div>
            )}

            {!isLoading && limitError && (
                <div className="flex flex-col items-center justify-center py-8 text-center space-y-3">
                    <LucideCircleX className="text-primary" size={40} />
                    <p className="text-white text-xl">Inventory unavailable</p>
                    <p className="text-neutral-400 max-w-sm">The inventory viewer isn't available on your current plan. Upgrade to inspect player inventories.</p>
                    <button
                        className="px-4 py-2 rounded-md bg-primary text-white hover:bg-primary-hover"
                        onClick={() => overlay.openErrorOverlay(limitError)}>
                        View upgrade options
                    </button>
                </div>
            )}

            {!isLoading && error && (
                <div className="flex flex-col items-center justify-center py-8 text-center space-y-2">
                    <LucideCircleSlash className="text-red-500" size={40} />
                    <p className="text-red-500 text-xl">Failed to load inventory</p>
                    {pluginUnresponsive && (
                        <p>Make sure the latest version of the <a className="text-primary hover:underline" rel="noreferrer" href="https://codefling.com/plugins/rust-server-tool" target="_blank">RustServerTool plugin</a> is installed on your server.</p>
                    )}
                    <p className="text-red-500 text-sm">{error}</p>
                </div>
            )}

            {!isLoading && inventory && (
                <>
                    <InventoryGrid label="Wear" slotCount={8} cols={8} slotPx={41} items={inventory.wear}/>
                    <InventoryGrid label="Main" slotCount={24} cols={6} items={inventory.main}/>
                    <InventoryGrid label="Belt" slotCount={6} cols={6} items={inventory.belt}/>
                </>
            )}
        </div>
    )
}
